<template>
    <div>
        <p-form @submit="saveDcc" id="dcc-edit-form" ref="dccForm">
            <b-card v-for="(dccElement, index) in dccElements"
                    :key="dccElement.dccLevel"
                    class="">
                <b-row cols="1" cols-md="2" cols-xl="3">
                        <b-col>
                            <p-input :label="dccElement.dccLevel + ' Name'"
                                    :disabled="dccElement.disabled"
                                    v-model="dccElement.name"
                                    rules="max:50|required" />
                        </b-col>
                    <b-col>
                        <p-number
                            :label="dccElement.dccLevel + ' Number'"
                            v-model.number="dccElement.number"
                            :disabled="dccElement.disabled || !dccElement.isNew"
                            input-type="integer"
                            rules="min_value:0|max_value:99|required|integer"
                        />
                    </b-col>
                    <b-col v-if="dccElement.dccLevel === 'Department'">
                        <p-select
                            dataType="productTypes"
                            label="DCC Type"
                            v-model.number="dccElement.type"
                            :disabled="dccElement.disabled"
                            rules="required|integer" />
                    </b-col>
                    <b-col>
                        <p-number
                            v-model="dccElement.desiredMarginPercent"
                            :disabled="dccElement.disabled"
                            label="Desired Margin (%)"
                            v-if="!dccElement.isNew || dccElement.dccLevel === 'Category'"
                            rules="p-decimal:10,2|min_value:0"
                            input-type="percent"
                            :placeholder="mixedPlaceholder(dccElement, 'desiredMarginPercent')"
                            @change="combinedCategorySettingChange(dccElement, 'desiredMarginPercent')"
                            :infoMessage="updateTooltip(dccElement)"
                        />
                    </b-col>
                    <b-col>
                        <p-select dataType="priceTagTypes"
                            :disabled="dccElement.disabled"
                                    label="Tag Type"
                                    :filter="tagTypeFilter"
                                    :no-selection-message="mixedPlaceholder(dccElement, 'priceTagTypeId')"
                                    :no-selection-disabled="true"
                                    v-if="!dccElement.isNew || dccElement.dccLevel === 'Category'"
                                    @change="combinedCategorySettingChange(dccElement, 'priceTagTypeId')"
                                    :infoMessage="updateTooltip(dccElement)"
                                    v-model="dccElement.priceTagTypeId" />
                    </b-col>
                    <b-col>
                        <p-select dataType="taxItems"
                            :disabled="dccElement.disabled"
                            label="Tax Item"
                            :no-selection-message="mixedPlaceholder(dccElement, 'taxItemId')"
                            :no-selection-disabled="true"
                            v-if="!dccElement.isNew || dccElement.dccLevel === 'Category'"
                            :rules='dccElement.dccLevel === "Category" ? "required" : ""'
                            @change="combinedCategorySettingChange(dccElement, 'taxItemId')"
                            :infoMessage="updateTooltip(dccElement)"
                            v-model="dccElement.taxItemId" />
                    </b-col>
                </b-row>                
                <b-row cols="1" cols-md="2" cols-xl="3" class="mt-3">
                    <b-col v-if="!dccElement.isNew">
                        <p-checkbox label="Is Active?"
                                    class=""
                                    use-indeterminate
                                    :disabled="dccElement.disabled"
                                    v-model="dccElement.isActive"
                                    @change="combinedCategorySettingChange(dccElement, 'isActive')"
                                    :infoMessage="updateTooltip(dccElement)" />
                    </b-col>
                    <b-col>
                        <p-checkbox class=""
                                    label="Allow Discounts"
                                    v-if="!dccElement.isNew || dccElement.dccLevel === 'Category'"
                                    use-indeterminate
                                    :disabled="dccElement.disabled"
                                    v-model.number="dccElement.allowsDiscounts"
                                    @change="combinedCategorySettingChange(dccElement, 'allowsDiscounts')"
                                    :infoMessage="updateTooltip(dccElement)" />
                    </b-col>                                        
                    <b-col>
                        <span v-if="dccElement.disabled" class="float-end ps-2">
                            <b-button class="mb-3 text-end"
                                    @click="enableEdit(dccElement)">Edit</b-button>
                        </span>
                        <span v-if="!dccElement.isNew" class="float-end ps-2" :title="deleteDisabledMessage(dccElement)">
                            <b-button class="mb-3 text-end"
                                variant="outline-danger"
                                :disabled="!!deleteDisabledMessage(dccElement)"
                                @click="deleteDcc(dccElement)">
                                Delete
                            </b-button>
                        </span>
                    </b-col>
                </b-row>
                <p-feature-flag
                    name="Accounting" 
                    >
                    <b-row v-if="(dccElement.dccLevel === 'Class' && !disableClass) || ( dccElement.dccLevel === 'Department' && !disableDepartment) || !dccElement.disabled" cols="1">
                        <b-col>
                        <GLCodeDcc
                            @chart-of-account-type-change="combinedCategoryChartOfAccountTypeSettingChange"
                            :item-dcc="dccElement"/>
                        </b-col>
                    </b-row>
                </p-feature-flag>
                <!-- hr to help keep things clear on smaller screens -->
                <hr v-if="index < dccElements.length - 1"  class="d-xl-none" />
            </b-card>
        </p-form> 
        <b-row>
            <b-col class="text-center">
                <b-button variant="primary" type="submit" form="dcc-edit-form">Save</b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import axios from "axios";
import NavigationGuard from '@/components/mixins/NavigationGuard.js';
import GLCodeDcc from '@/components/GLCodeDcc';
import { useSelectListsStore } from '@/stores/selectLists.js';
import { PRODUCT_TYPE_VALUE } from '@/components/Common/Constants.js';

export default {
    name: "edit-dcc",
    mixins: [NavigationGuard],
    components: {
        GLCodeDcc
        },  
    props: {
        selectedDcc: [Object],
        dccs: [Array]        
    },
    data() {
        return {
            dccElements: []
        };
    },
    computed: {
        disableDepartment() {
            return (
                !this.selectedDcc.department.isNew &&
                !!this.selectedDcc.class
            );
        },
        disableClass() {
            return (
                !this.selectedDcc.class.isNew &&
                !!this.selectedDcc.category
            );
        },
        updatedDepartment() {
            return this.dccElements[0];
        },
        updatedCategory() {
            return this.dccElements.find(x => x.dccLevel === "Category");
        },
        updatedClass() {
            return this.dccElements.find(x => x.dccLevel === "Class");
        },
        allClassCategories(){
            return this.selectedDcc?.class?.dccCategories ?? [];
        },
        allDepartmentClasses(){
            return this.selectedDcc?.department?.dccClasses ?? [];
        },
        allDepartmentCategories(){
            return this.selectedDcc?.department?.dccClasses?.flatMap(x => x.dccCategories) ?? [];
        },
        accountTypeEntries() {
            return useSelectListsStore().lists.accountTypeEntries ?? [];
        },
        tagTypeFilter() {
            // Package DCCs share GM's tag types, so map Package -> GM (Common) for
            // filtering; Book and GM filter by their own type as-is.
            const type = this.updatedDepartment?.type;
            return type === PRODUCT_TYPE_VALUE.PACKAGE ? PRODUCT_TYPE_VALUE.COMMON : type;
        }
    },
    methods: {
        updateTooltip(dccElement){
            if(dccElement.disabled) {return null;}
            if(dccElement.dccLevel === "Category") {return null;}

            let categoryCount = dccElement.dccLevel === "Department" ? this.allDepartmentCategories.length : this.allClassCategories.length;
            let countMessage = categoryCount > 1 ? `all ${categoryCount} categories` : `the ${categoryCount} category `

            if(!this.updatedCategory || categoryCount < 2){
                return `Updates ${countMessage} belonging to this ${dccElement.dccLevel.toLowerCase()}.`;
            }
            return `Updates ${countMessage} belonging to this ${dccElement.dccLevel.toLowerCase()}, not just the category shown below.`
        },
        saveDcc() {
            let data = {
                dccDepartment: this.updatedDepartment
            };
            if(data.dccDepartment.disabled ){
                //we don't need to spend time updating every category in this department if
                //department level properties weren't updated.
                this.removeCombinedCategorySettings(data.dccDepartment);
            }

            if (this.updatedClass) {
                data.dccDepartment.dccClass = this.updatedClass;
                if(data.dccDepartment.dccClass.disabled){
                    this.removeCombinedCategorySettings(data.dccDepartment.dccClass);
                }
            }
            if (this.updatedCategory) {
                data.dccDepartment.dccClass.dccCategory = this.updatedCategory;
            }

            axios.post("dccs", data).then(() => {
                if (this.dccElements.some(x => x.isNew)) {
                    // Refresh IsNew accurately
                    this.$emit("reload-dccs", {
                        departmentNumber: this.updatedDepartment.number,
                        classNumber: this.updatedClass?.number,
                        categoryNumber: this.updatedCategory?.number
                    });
                }else{
                    //update the dccs, to avoid a page reload
                    if (this.updatedDepartment) {
                        this.updateDccElement(
                            this.updatedDepartment,
                            this.selectedDcc.department,
                            this.allDepartmentCategories
                        );
                    }
                    if (this.updatedClass) {
                        this.updateDccElement(
                            this.updatedClass,
                            this.selectedDcc.class,
                            this.allClassCategories
                        );
                    }
                    if (this.updatedCategory) {
                        this.updateDccElement(
                            this.updatedCategory,
                            this.selectedDcc.category
                        );
                    }
                }

                this.$appSuccess("DCC saved successfully.");
                this.dataLoaded();
            });            
        },
        updateDccElement(source, destination, childCategories) {
            destination.name = source.name;
            destination.number = source.number;
            destination.type = source.type;
 
            let isChanged = source?.dccCategoryChartOfAccountsTypes.locations.some(loc => {return loc.dccChartsOfAccountTypes.some(dccChartsOfAccountType => dccChartsOfAccountType.isChanged === true);}); 
            
            if (source?.dccLevel === "Department"){
                destination.dccClasses.forEach(x => this.mergeChartsOfAccount(source, x));
            }

            //update these properties for any child categories when they have
            //been set at either the department or class level.
            [destination, ...(childCategories ?? [])]?.forEach(destination => {
                
                if(source?.desiredMarginPercent != null){
                    destination.desiredMarginPercent = source.desiredMarginPercent;
                }
                if(source?.allowsDiscounts != null){
                    destination.allowsDiscounts = source.allowsDiscounts;
                }
                if(source?.isActive != null){
                    destination.isActive = source.isActive;
                }
                if(source?.priceTagTypeId != null){
                    destination.priceTagTypeId = source.priceTagTypeId;
                }
                if(source?.taxItemId != null){
                    destination.taxItemId = source.taxItemId;
                }                             
                if(isChanged === true){
                    this.mergeChartsOfAccount(source, destination);
                }
            });
        },
        // Copy resolved GL selections from a Department/Class onto a child, but
        // skip any "Mixed" (-1) sentinel so a mixed parent value doesn't
        // overwrite — and visibly blank to "----Mixed----" — a child's own GL
        // code. (Bug: saving at Dept/Class made every Category GL show Mixed
        // until a reload, because the whole config, -1s included, was copied.)
        mergeChartsOfAccount(source, destination) {
            const sourceLocations = source?.dccCategoryChartOfAccountsTypes?.locations ?? [];
            const destContainer = destination?.dccCategoryChartOfAccountsTypes;
            if (!destContainer || !Array.isArray(destContainer.locations)) {
                return;
            }
            sourceLocations.forEach(srcLoc => {
                const destLoc = destContainer.locations.find(l => l.locationId === srcLoc.locationId);
                if (!destLoc) {
                    return;
                }
                (srcLoc.dccChartsOfAccountTypes ?? []).forEach(srcType => {
                    if (srcType.chartOfAccountsId === -1) {
                        return; // Mixed — keep the child's own value
                    }
                    const destType = (destLoc.dccChartsOfAccountTypes ?? [])
                        .find(t => t.chartOfAccountsTypeId === srcType.chartOfAccountsTypeId);
                    if (destType) {
                        destType.chartOfAccountsId = srcType.chartOfAccountsId;
                    }
                });
            });
        },
        setChartOfAccountSetting(dccElements, locationItem){
            if(dccElements.length > 0)
            {
                dccElements.map(dccElement => { 
                        var location = dccElement.dccCategoryChartOfAccountsTypes.locations.filter(n => n.locationId === locationItem.locationId);
                        location[0].dccChartsOfAccountTypes.map(x => x.chartOfAccountsId = locationItem.dccChartsOfAccountTypes.filter( y => y.chartOfAccountsTypeId === x.chartOfAccountsTypeId)[0].chartOfAccountsId);
                    });
            }
            else
            {
                var location = dccElements.dccCategoryChartOfAccountsTypes.locations.filter(n => n.locationId === locationItem.locationId);
                location[0].dccChartsOfAccountTypes.map(x => x.chartOfAccountsId = locationItem.dccChartsOfAccountTypes.filter( y => y.chartOfAccountsTypeId === x.chartOfAccountsTypeId)[0].chartOfAccountsId);
            }
          
        },
        combinedCategoryChartOfAccountTypeSettingChange(dccElement, locationItem){      
            if (dccElement.dccLevel === "Department"){                                
                this.setChartOfAccountSetting(dccElement,locationItem);
            }
            if (dccElement.dccLevel === "Class"){                
                this.setChartOfAccountSetting(dccElement,locationItem);
            }
        },
        getCombinedCategorySetting(categories, propertyName){
            return  categories?.every(x => x === undefined ||  x[propertyName] === categories[0]?.[propertyName])
                        ? categories[0]?.[propertyName] : null;
        },
        // True when the children categories hold more than one distinct value for
        // this shared field (i.e. genuinely "mixed", not just all-unset).
        isCombinedMixed(categories, propertyName){
            const values = (categories ?? []).filter(x => x !== undefined).map(x => x[propertyName]);
            if (values.length < 2) return false;
            return !values.every(v => v === values[0]);
        },
        // Mirror the GL dropdowns: when a shared (combined-category) field is
        // mixed across the children of this Department/Class, surface
        // "----Mixed----" as the field's placeholder. Returns null otherwise so
        // the field falls back to its normal default. Uses the same live set as
        // combinedCategorySettingChange (the edited category + the originals) so
        // it reflects an in-progress Category change immediately, not only after
        // save.
        mixedPlaceholder(dccElement, propertyName){
            let categories;
            if (dccElement.dccLevel === "Department") {
                categories = [this.updatedCategory, ...this.allDepartmentCategories];
            } else if (dccElement.dccLevel === "Class") {
                categories = [this.updatedCategory, ...this.allClassCategories];
            } else {
                return null;
            }
            return this.isCombinedMixed(categories, propertyName) ? '----Mixed----' : null;
        },
        getCombinedCategorySettings(categories) {
            //some category level properties are allowed to be set the department/class level.
            //we get the single value if they are all the same, otherwise we set it to null.
            return {
                desiredMarginPercent : this.getCombinedCategorySetting(categories, 'desiredMarginPercent'),
                allowsDiscounts : this.getCombinedCategorySetting(categories, 'allowsDiscounts'),
                isActive : this.getCombinedCategorySetting(categories, 'isActive'),
                priceTagTypeId : this.getCombinedCategorySetting(categories, 'priceTagTypeId'),
                taxItemId : this.getCombinedCategorySetting(categories, 'taxItemId'),                    
            }
        },
        removeCombinedCategorySettings(dccElement){
            delete dccElement.desiredMarginPercent;
            delete dccElement.allowsDiscounts;
            delete dccElement.isActive;
            delete dccElement.priceTagTypeId;
            delete dccElement.taxItemId;
           
        },
        enableEdit(dccElement) {
            dccElement.disabled = false;
            this.dataLoaded();
        },
        // A department requires class, and a class requires a category, so the last of either isn't deletable. 
        // Shows a reason tooltip when the delete button is disabled.
        deleteDisabledMessage(dccElement) {
            if (dccElement.inUse) {
                return `This ${dccElement.dccLevel.toLowerCase()} is in use and cannot be deleted.`;
            }
            if (dccElement.dccLevel === "Class" && this.allDepartmentClasses.length <= 1) {
                return "A department must have at least one class.";
            }
            if (dccElement.dccLevel === "Category" && this.allClassCategories.length <= 1) {
                return "A class must have at least one category.";
            }
            return null;
        },
        deleteDcc(dccElement) {
            this.$bvModal
                .msgBoxConfirm(this.deleteWarning(dccElement))
                .then(value => {
                    if (!value) {
                        return;
                    }
                    this.dataLoaded();
                    this.$emit("delete-dcc", dccElement.dccLevel);
                });
        },
        // Deleting a Department or Class also deletes everything beneath it so include a warning showing the counts of what will be deleted.
        deleteWarning(dccElement) {
            let message = `the "${dccElement.name}" ${dccElement.dccLevel.toLowerCase()}`;
            if (dccElement.dccLevel === "Category") {
                return `Are you sure you want to delete ${message}?`;
            }
            if (dccElement.dccLevel === "Class") {
                return `Deleting ${message} also deletes the ${this.pluralize(this.allClassCategories.length, "Category", "Categories")} beneath it. Are you sure you wish to delete all of them?`;
            }
            return `Deleting ${message} also deletes the ${this.pluralize(this.allDepartmentClasses.length, "Class", "Classes")} and ${this.pluralize(this.allDepartmentCategories.length, "Category", "Categories")} beneath it.  Are you sure you wish to delete all of them?`;
        },
        pluralize(count, singular, plural) {
            return `${count} ${count === 1 ? singular : plural}`;
        },
        combinedCategorySettingChange(parentElement, updatedPropertyName){
            let newValue = parentElement[updatedPropertyName];

            //don't have a cateogory if we selected a dept or class.
            if(this.updatedCategory){
                this.updatedCategory[updatedPropertyName] = newValue;
            }

            if(parentElement.dccLevel === "Department" && this.updatedClass){
                this.updatedClass[updatedPropertyName] = newValue;
            }
            else if(parentElement.dccLevel === "Class"){
                this.updatedDepartment[updatedPropertyName] = this.getCombinedCategorySetting([this.updatedCategory, ...this.allDepartmentCategories], updatedPropertyName);
            }
            else if(parentElement.dccLevel === "Category"){
                this.updatedDepartment[updatedPropertyName] = this.getCombinedCategorySetting([this.updatedCategory, ...this.allDepartmentCategories], updatedPropertyName);
                this.updatedClass[updatedPropertyName] = this.getCombinedCategorySetting([this.updatedCategory, ...this.allClassCategories], updatedPropertyName);
            }

            this.$nextTick(() =>{
                this.$refs.dccForm?.reset();
            });
        },
        loadDccElements() {
            if (!this.selectedDcc.department) {
                return;
            }
            //find what elements should be rendered, and configure their display mode
            //I wanted this to be computed, but then it isn't reactive when binding
            //to v-model (causing validation issues)
            var elements = [
                {
                    dccLevel: "Department",                    
                    disabled: this.disableDepartment,
                    ...this.selectedDcc.department,
                    ...this.getCombinedCategorySettings(this.allDepartmentCategories)                    
                }
            ];
            let classSettings = (this.selectedDcc.class?.isNew ?
                        this.getCombinedCategorySettings(this.allDepartmentCategories)
                        : this.getCombinedCategorySettings(this.allClassCategories))
            if (this.selectedDcc.department.isNew || this.selectedDcc.class) {
                elements.push({
                    dccLevel: "Class",
                    disabled: this.disableClass,
                    ...this.selectedDcc.class,
                    ...classSettings
                });
            }

            if (elements.some(x => x.isNew) || this.selectedDcc.category) {
                let parentDefaults = this.selectedDcc.category?.isNew ? classSettings : {}
                elements.push({
                    dccLevel: "Category",
                    disabled: false,
                    ...this.selectedDcc.category,
                    ...parentDefaults
                });
            }
            this.dccElements = JSON.parse(JSON.stringify(elements));
                        
            var allMatch = ((object, typeId, locationId) => {
                var chartOfAccountIds = [];
                object.forEach(item => {
                    item.dccCategoryChartOfAccountsTypes.locations.find(x => x.locationId == locationId).dccChartsOfAccountTypes
                        .filter(coat => coat.chartOfAccountsTypeId == typeId)
                            .forEach(coa => {
                                chartOfAccountIds.push(coa.chartOfAccountsId);
                            });
                         });
                return Object.keys(chartOfAccountIds).length !== 0 && chartOfAccountIds.every(val => val === chartOfAccountIds[0]) ? chartOfAccountIds[0] : -1;
            });

            var allChartOfAccountsTypes = this.accountTypeEntries.map(x => x.value);

            if(this.selectedDcc.class)
            {
                var classElement = this.dccElements[1];

                allChartOfAccountsTypes.forEach(coaType => {
                    var chartOfAccountId = allMatch(classElement.dccCategories, coaType, this.selectedLocation);
                    
                    var classType = classElement.dccCategoryChartOfAccountsTypes?.locations
                        .find(x => x.locationId == this.selectedLocation).dccChartsOfAccountTypes.find(y => y.chartOfAccountsTypeId == coaType);

                    if(classType)
                    {
                        classType.chartOfAccountsId = chartOfAccountId;
                    }
                });
            }
            else if(this.selectedDcc.department)
            {   
                var departmentElement = this.dccElements[0];

                allChartOfAccountsTypes.forEach(coaType => {
                    departmentElement.dccClasses?.forEach(dccClass => {
                        var classChartOfAccountId = allMatch(dccClass.dccCategories, coaType, this.selectedLocation);
                        
                        var classType = dccClass.dccCategoryChartOfAccountsTypes?.locations
                            .find(x => x.locationId == this.selectedLocation).dccChartsOfAccountTypes.find(y => y.chartOfAccountsTypeId == coaType);
                                                    
                        if(classType)
                        {
                            classType.chartOfAccountsId = classChartOfAccountId;
                        }
                    });                    

                    if(departmentElement.dccClasses)
                    {
                        var deptChartOfAccountId = allMatch(departmentElement.dccClasses, coaType, this.selectedLocation);
                        var locations = departmentElement.dccCategoryChartOfAccountsTypes?.locations.find(x => x.locationId == this.selectedLocation);
                        var departmentType = locations.dccChartsOfAccountTypes.find(y => y.chartOfAccountsTypeId == coaType);
                        
                        if(departmentType)
                        {                            
                            departmentType.chartOfAccountsId = deptChartOfAccountId;                         
                        }
                    }
                });
            }
            
            this.dataLoaded();
        }
    },
    watch: {
        dccElements: NavigationGuard.$watcher,
        selectedDcc: {
            handler: function() {
                this.loadDccElements();            
            },
            deep: true,
            immediate: true
        }
    }
};
</script>
